import { OnboardingLayout, useSlideSetup } from '@/features/client-onboarding';
import { OnboardingCostFactors } from '@/pages/OnboardingCostFactors';
import { OnboardingD2CPaywall } from '@/pages/OnboardingD2CPaywall';
import { OnboardingD2CPaymentSuccess } from '@/pages/OnboardingD2CPaymentSuccess';
import { selectClient, selectSession } from '@/model';
import { useSelector } from 'react-redux';
import { useAppMode } from '@/hooks/use-app-mode';
import { MapPin, TrendingUp, Clock, Heart } from 'lucide-react';
import type { ReactNode } from 'react';
import { InsightHighlightedTitle } from '@/components/InsightHighlightedTitle';

const INSIGHT_ICONS: Record<string, ReactNode> = {
  'cost-of-living': <MapPin className="text-primary size-5" />,
  'inflation-horizon': <TrendingUp className="text-primary size-5" />,
  'likelihood': <Clock className="text-primary size-5" />,
  'family-caregiving': <Heart className="text-primary size-5" />,
};

const INSIGHT_ORDER = ['cost-of-living', 'inflation-horizon', 'likelihood', 'family-caregiving'];

export function OnboardingInsightsSummary({ clientId }: ClientIdProps) {
  const client = useSelector(selectClient)!;
  const { clientInsights } = client;
  const { clientStripe } = useSelector(selectSession);
  const appMode = useAppMode();

  // The d2c app mode always continues to the paywall slide, which opens the guided flow.
  const hasActivePlan = (clientStripe?.hasActivePlan ?? false) && appMode !== 'd2c';

  useSlideSetup({
    slidePage: OnboardingInsightsSummary,
    nextButtonLabel: 'Next',
    nextSlidePage: hasActivePlan ? OnboardingD2CPaymentSuccess : OnboardingD2CPaywall,
  });

  const orderedInsights = INSIGHT_ORDER.map(id => clientInsights?.find(i => i.id === id)).filter(
    Boolean,
  ) as ClientInsight[];

  return (
    <OnboardingLayout>
      <div className="flex w-full flex-col py-6 md:py-16">
        <h1 className="text-balance text-center text-2xl font-bold text-gray-950 md:text-4xl">
          Here&apos;s how your predictions stack up.
        </h1>
        <p className="text-foreground mt-3 text-center text-sm leading-relaxed md:text-base">
          You&apos;re unique in so many ways. This impacts what your future long-term care may look
          like, and what it will likely cost.
        </p>
        <p className="text-muted-foreground mt-8 text-center text-xs italic leading-relaxed md:text-sm">
          Waterlily is built to personalize and optimize your plan to all the uniqueness that is
          you.
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {orderedInsights.map(insight => (
            <div
              key={insight.id}
              className="border-primary-200 flex items-start gap-3 rounded-xl border bg-white p-5 shadow-sm"
            >
              <div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-full">
                {INSIGHT_ICONS[insight.id]}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  {insight.categoryLabel}
                </p>
                <p className="mt-1 text-sm font-semibold leading-snug text-gray-950">
                  <InsightHighlightedTitle
                    title={insight.title}
                    highlights={insight.titleHighlights}
                  />
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
                  {insight.summaryDescription}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </OnboardingLayout>
  );
}
